import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Alert,
    CircularProgress,
    Grid,
} from '@mui/material';
import { useState, useEffect } from 'react';
import {
    usersApi,
    User,
    UserRole,
    UserStatus,
    CreateUserRequest,
    UpdateUserRequest,
} from '../services/users-api.service';
import { ApiError } from '../services/base-api.service';

interface UserFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    user?: User | null;
    mode: 'create' | 'edit';
}

interface FormData {
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    status: UserStatus;
}

const initialFormData: FormData = {
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
    status: 'active',
};

const roleOptions: { value: UserRole; label: string }[] = [
    { value: 'admin', label: 'Admin' },
    { value: 'developer', label: 'Developer' },
    { value: 'finance', label: 'Finance' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'user', label: 'User' },
];

const statusOptions: { value: UserStatus; label: string }[] = [
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
];

export function UserFormDialog({ open, onClose, onSave, user, mode }: UserFormDialogProps) {
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Set form data when modal opens
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && user) {
                setFormData({
                    email: user.email,
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    role: user.role,
                    status: user.status,
                });
            } else {
                setFormData(initialFormData);
            }
            setError(null);
            setFormErrors({});
        }
    }, [open, mode, user]);

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Clear field error when user starts typing
        if (formErrors[field]) {
            setFormErrors((prev) => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email.trim())) {
                errors.email = 'Invalid email format';
            }
        }

        if (!formData.role) {
            errors.role = 'Role is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            setError(null);

            if (mode === 'create') {
                const request: CreateUserRequest = {
                    email: formData.email.trim(),
                    firstName: formData.firstName.trim() || undefined,
                    lastName: formData.lastName.trim() || undefined,
                    role: formData.role,
                };
                const response = await usersApi.createUser(request);
                if (!response.success) {
                    throw new Error('Failed to create user');
                }
            } else if (user) {
                const request: UpdateUserRequest = {
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    role: formData.role,
                    status: formData.status,
                };
                const response = await usersApi.updateUser(user.id, request);
                if (!response.success) {
                    throw new Error('Failed to update user');
                }
            }

            onSave();
            onClose();
        } catch (err) {
            if (err && typeof err === 'object' && 'message' in err) {
                setError((err as ApiError).message);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Failed to save user');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    const title = mode === 'create' ? 'Create User' : 'Edit User';
    const saveButtonText = mode === 'create' ? 'Create' : 'Update';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                                disabled={mode === 'edit'}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                error={!!formErrors.firstName}
                                helperText={formErrors.firstName}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                error={!!formErrors.lastName}
                                helperText={formErrors.lastName}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!!formErrors.role}>
                                <InputLabel id="role-label">Role</InputLabel>
                                <Select
                                    labelId="role-label"
                                    value={formData.role}
                                    label="Role"
                                    onChange={(e) => handleInputChange('role', e.target.value)}
                                >
                                    {roleOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {mode === 'edit' && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="status-label">Status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        value={formData.status}
                                        label="Status"
                                        onChange={(e) => handleInputChange('status', e.target.value)}
                                    >
                                        {statusOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {saving ? 'Saving...' : saveButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
